import { useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { useIsIndividual } from '@/hooks/useIsIndividual';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { genNameInitials } from '@/utils/name';
import Combobox from '../index';
import type { ComboboxOption, ComboboxOptionRenderer } from '../types';
import { useWorkersForSelect } from './-query';
import type { Category } from './-query/type';

/**
 * DressCodeのPerson情報を表す型
 */
export type Person = Readonly<{
  id: string;
  name: string;
  nameInitials: string;
  email: string;
  avatarUrl: string;
}>;
interface PersonSelectorComboboxProps {
  onSelect: (person: Person) => void;
  disabledPersonIds?: Set<string>;
  onClose: () => void;
  /**
   * カテゴリフィルタ（例: CATEGORY_FILTER.ALL_ALL で離籍者を除外）
   */
  category?: Category;
  /** メールアドレス未登録のメンバーを除外する */
  excludeNotHaveWorkerEmail?: boolean;
  /** 検索テキスト変更時のコールバック */
  onSearchTextChange?: (text: string) => void;
  /** 検索結果のスクロール末尾到達時のコールバック */
  onScrollToEnd?: () => void;
  /** 検索文字数の下限（満たすまで検索しない） */
  minSearchLength?: number;
}
const renderPersonOption: ComboboxOptionRenderer<ComboboxOption> = ({
  option
}) => <HStack className="items-center gap-3">
    <Avatar className="size-6">
      <AvatarImage src={option.avatarUrl ?? undefined} alt={option.label} />
      <AvatarFallback>{genNameInitials(option.label)}</AvatarFallback>
    </Avatar>
    <VStack>
      <Text variant="labelSmall">{option.label}</Text>
      {option.email && <Text variant="labelSmall" className="text-muted-foreground">
          {option.email}
        </Text>}
    </VStack>
  </HStack>;
export default function PersonSelectorCombobox({
  onSelect,
  disabledPersonIds = new Set(),
  onClose,
  category,
  excludeNotHaveWorkerEmail,
  onSearchTextChange,
  onScrollToEnd,
  minSearchLength = 0
}: PersonSelectorComboboxProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.Combobox.personSelectorCombobox'
  });
  const isIndividual = useIsIndividual();
  const [searchText, setSearchText] = useState('');
  const [value, setValue] = useState<string | null>(null);
  const valueToLabelCache = useValueToLabelCache();
  const shouldFetch = searchText.length >= minSearchLength;
  const {
    data: options = [],
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useWorkersForSelect(searchText, disabledPersonIds, category, {
    enabled: shouldFetch,
    isIndividual,
    excludeNotHaveWorkerEmail
  });
  const displayedOptions = shouldFetch ? options : [];
  const handleValueChange = (newValue: string | null) => {
    if (!newValue) return;
    setValue(newValue);
    const selectedOption = displayedOptions.find((option: {
      value: string;
    }) => option.value === newValue);
    if (!selectedOption) return;
    onSelect({
      id: selectedOption.value,
      name: selectedOption.label,
      nameInitials: genNameInitials(selectedOption.label),
      email: selectedOption.email ?? '',
      avatarUrl: selectedOption.avatarUrl ?? ''
    });
    onClose();
  };
  const handleScrollToEnd = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
    onScrollToEnd?.();
  };
  return <Combobox type="single" value={value} options={displayedOptions} isFetching={shouldFetch && (isLoading || isFetchingNextPage)} onValueChange={handleValueChange} searchPlaceholder={t('searchPersonPlaceholder')} onSearchChange={useDebouncedCallback(text => {
    setSearchText(text);
    onSearchTextChange?.(text);
  }, [onSearchTextChange])} valueToLabelCache={valueToLabelCache} renderOption={renderPersonOption} onScrollToEnd={handleScrollToEnd} />;
}